<template>
    <div :class="['table', theme]">
        <VisualInformative v-if="hasDataButAllHidden" message="Current filter has nothing to show" />

        <template v-else>
            <div class="table-row header-row" :style="{ 'grid-template-columns': gridTemplateColumns }">
                <div v-if="selectionEnabled" @click="toggleSelection()" :class="['checkmark', { 'selected': hasSelectedItems }]">
                    <i class="material-icons">{{ allItemsSelected ? 'check' : 'remove' }}</i>
                </div>

                <div v-for="(headerColumnName, index) of headerRow" :key="index">{{ headerColumnName }}</div>
            </div>

            <div v-for="(row, index) in rows" :key="index"
                 v-if="row.isVisible"
                 :class="['table-row', { 'are-functions-disabled': row.getAreFunctionsDisabled() } ]"
                 :style="{ 'grid-template-columns': gridTemplateColumns }">
                <div v-if="selectionEnabled"
                     @click="row.getAreFunctionsDisabled() ? '' : toggleSelection(index)"
                     :class="['checkmark', { 'selected': !!row.selected, 'selection-disabled-without-explanation': row.getAreFunctionsDisabled() && !!!row.getWhyAreFunctionsDisabled() }]">
                    <i class="material-icons">check</i>

                    <div class="tool-tip-box"><InformativeTooltip :text="row.getWhyAreFunctionsDisabled()" position="right"/></div>
                </div>

                <div v-for="(column, index) of row.getItems()" :key="index" :class="transformColumnToClass(column, index)">
                    <a v-if="!!column && typeof column === 'object'" :href="column.url" :target="column.target || '_blank'">{{ column.text }}</a>
                    <span v-else>{{ column }}</span>
                    <div @click="column.callback()" class="question-modal-text">{{ column.title }}</div>
                </div>

                <div v-if="row.hasFunctionsForRow" class="custom-inline-buttons">
                    <i v-for="(func, index) of row.getFunctions()" :key="index" @click="func.callback()" class="material-icons">{{ func.icon }}</i>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import InformativeTooltip from "../../results/v2-components/Onboarding/InformativeTooltip";
    import VisualInformative from "@/components/superadmin/ux/VisualInformative";
    import { kebabCase } from "@/mixins/stringFormatting"

    export default {
        name: "CustomTable",
        components: {VisualInformative, InformativeTooltip},
        props: {
            selectionEnabled: {
                type: Boolean,
                default: false,
            },
            disabledColumnToClassIndexes: {
                type: Array,
                default: [],
            },
            headerRow: Array,
            rows: Array,
            rowWidths: Array,
            theme: {
                type: String,
                default: ''
            }
        },
        watch: {
            allItemsSelected(areAllItemsSelected) {
                if (areAllItemsSelected) return;

                this.deselectAllDisabledRows();
            },
            rows: {
                handler() { this.updateHasSelectedItems() },
                deep: true,
            },
            selectionEnabled() {
                this.prepareGridTemplateColumns()
            },
        },
        computed: {
            hasDataButAllHidden() {
                return this.rows.length > 0 && this.rows.filter(row => row.isVisible).length < 1
            }
        },
        data() {
            return {
                allItemsSelected: false,
                hasSelectedItems: false,
                gridTemplateColumns: "",
            }
        },
        mounted() {
            this.prepareGridTemplateColumns();
        },
        methods: {
            transformColumnToClass(column, index) {
                if (this.disabledColumnToClassIndexes.includes(index) || !column) return;

                return kebabCase(column.text || column)
            },

            prepareGridTemplateColumns() {
                this.gridTemplateColumns = "";

                if (this.selectionEnabled) {
                    this.gridTemplateColumns += "24px";
                }

                for(const width of this.rowWidths) {
                    this.gridTemplateColumns += ` ${width}`;
                }
            },

            toggleSelection(index) {
                if (index >= 0){
                    this.rows[index].selected = !this.rows[index].selected
                } else {
                    this.rows.map(row => {
                        row.isVisible && !row.getAreFunctionsDisabled() ? row.selected = !this.hasSelectedItems : row.selected = false
                    });
                }

                this.updateHasSelectedItems();
            },

            updateHasSelectedItems() {
                const selectedItems = this.rows.filter(row => !!row.selected === true).length;
                const allItems = this.rows.length;
                this.hasSelectedItems = selectedItems > 0;
                this.allItemsSelected = allItems <= selectedItems;
                this.$emit('hasSelectedItems', this.hasSelectedItems);

                const selectedItemIndexes = this.getSelectedIndexes();
                this.$emit('selectedItemIndexes', selectedItemIndexes);

                this.$forceUpdate();
            },

            getSelectedIndexes() {
                return this.rows.map((row, index) => {
                    if (!!row.selected === true) return index;
                }).filter(index => index !== undefined);
            },

            deselectAllDisabledRows() {
                this.rows.map(row => {
                    if (row.getAreFunctionsDisabled()) row.selected = false;
                });
                this.updateHasSelectedItems();
            },
        }
    }
</script>

<style lang="scss">
    .table {
        margin-top: 20px;

        &.error {
            .table-row.header-row {
                background: $error;
            }
        }

        .table-row {
            position: relative;
            display: grid;
            padding: 8px;
            font-size: 14px;
            line-height: 20px;

            border-bottom: 1px solid rgba($black-1, .25);

            &:hover:not(.header-row) {
                background: rgba($black-1, .1);

                &.are-functions-disabled .checkmark.selection-disabled-without-explanation {
                    opacity: 0
                }

                .custom-inline-buttons {
                    display: block;
                }
            }

            & > div:not(.custom-inline-buttons) {
                margin-right: 8px;
            }

            a {
                color: inherit;
                text-decoration: underline;
            }

            .tool-tip-box {
                position: relative;
                display: none;
            }

            &.are-functions-disabled {
                .checkmark {
                    border-color: rgba($black-1, .65);
                    cursor: default;

                    &.selected {
                        background: rgba($black-1, .65);
                    }

                    &.selection-disabled-without-explanation {
                        border-color: transparent;
                    }

                    &:hover {
                        .tool-tip-box {
                            display: block;

                            .informative-tooltip {
                                width: max-content;
                                left: 16px;

                                .tool-tip {
                                    .text {
                                        padding: 0;
                                    }

                                    .buttons {
                                        display: none;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            &.header-row {
                background: $black-1;
                color: #fff;
                font-weight: bold;
            }

            .custom-inline-buttons {
                position: absolute;
                top: 50%;
                right: 8px;
                transform: translateY(-50%);
                height: 20px;
                display: none;

                i {
                    margin-left: 4px;
                    font-size: 20px;
                    color: rgba($black-1, .5);
                    cursor: pointer;

                    &:hover {
                        color: rgba($black-1, .75)
                    }
                }
            }
        }

        .checkmark {
            $checkmark-size: 16px;
            width: $checkmark-size;
            height: $checkmark-size;
            margin: 2px 8px 0 0;

            display: flex;
            justify-content: center;
            align-items: center;
            flex-shrink: 0;

            border: 2px solid $green;
            border-radius: 3px;
            background: #fff;
            cursor: pointer;

            &.selected {
                background: $green;

                i {
                    display: block;
                }
            }

            i {
                display: none;
                font-size: 14px;
                color: #fff;
            }
        }
    }
</style>